import { Table } from '@mantine/core';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { ICurrency } from '../../../libraries/currencies';
import { BankTransactionDenom } from '../../pages/bank-transactions/process-bank-data';
import { ReadValues } from '../../../libraries/transactions';
import { IQuantities, getCurrencyValue } from '../../utils/script';
import './styles.scss';

interface Props {
  currency: ICurrency;
  cash: BankTransactionDenom[]; // Preprocessed bank denominations
  values?: ReadValues[]; // Optional values table
}

// Prepare quantities for all denominations
export const prepareQuantitiesUnified = (cash: BankTransactionDenom[], currency: ICurrency): IQuantities[] => {
  if (!currency) return [];

  return currency.denominations.map(denom => {
    const matchedCash = cash.find(c => c.cid === currency.cid && c.cindex === denom.index);
    const qty = matchedCash ? (matchedCash.received ?? 0) + (matchedCash.dispensed ?? 0) : 0;
    const value = denom.value * currency.factor;
    const amount = Number((qty * value).toFixed(2));

    return {
      quantity: qty,
      value,
      amount,
      cindex: denom.index
    };
  });
};

const BankTransactionTable = ({ currency, cash, values = [] }: Props) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);

  // Generate columns with all denominations
  const columns: IQuantities[] = prepareQuantitiesUnified(cash, currency);

  return (
    <Table.ScrollContainer minWidth={500}>
      {/* DENOMINATIONS TABLE */}
      <Table withColumnBorders withTableBorder striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th rowSpan={2}>{currency.currencyStr}</Table.Th>
            <Table.Th colSpan={currency.nCoins}>{lang.GLOBAL_COINS}</Table.Th>
            <Table.Th colSpan={currency.nBills}>{lang.GLOBAL_BILLS}</Table.Th>
          </Table.Tr>
          <Table.Tr>
            {columns.map((col, index) => (
              <Table.Td key={index}>{col.amount.toFixed(2)}</Table.Td>
            ))}
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          <Table.Tr>
            <Table.Td>{lang.GLOBAL_QUANTITY}</Table.Td>
            {columns.map((col, i) => (
              <Table.Td key={i}>{col.quantity}</Table.Td>
            ))}
          </Table.Tr>

          <Table.Tr>
            <Table.Td>{lang.GLOBAL_AMOUNT}</Table.Td>
            {columns.map((col, i) => (
              <Table.Td key={i}>{col.amount.toFixed(2)}</Table.Td>
            ))}
          </Table.Tr>
        </Table.Tbody>
      </Table>

      {/* VALUES TABLE */}
      {values.length > 0 && (
        <Table mt="md" withColumnBorders striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_DENOMINATION}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_SERIAL_NUMBER}</Table.Th>
              <Table.Th>{lang.GLOBAL_ACCEPTED}</Table.Th>
              <Table.Th>{lang.TRANSACTION_SUSPECT}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {values.map((v, i) => (
              <Table.Tr key={i}>
                <Table.Td>{getCurrencyValue(v.cid, v.cindex)}</Table.Td>
                <Table.Td>{v.serial}</Table.Td>
                <Table.Td>{v.rejected ? 'X' : '✓'}</Table.Td>
                <Table.Td>{v.suspicious ? 'YES' : '-'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Table.ScrollContainer>
  );
};

export default BankTransactionTable;
